import { Circle, DriveFileMoveOutlined } from '@mui/icons-material'
import { CreativeUploadFolder, useCreativeUploadPage } from './_main'
import { FC, useEffect, useRef } from 'react'
import { FileAction, FileState } from 'components/common/FileAPI'
import { Grid, Stack, Typography } from '@mui/material'
import { Trans, useTranslation } from 'react-i18next'

import { BEIGE_700 } from 'constants/styling/theme'
import { CreativeUploadImageItem } from './CreativeUploadImageItem.comonent'
import { FileAPIUpload } from 'components/common/FileAPIUpload'
import { LightroomPublishRow } from './LightroomPublishRow.component'
import { MUIButton } from 'components/common/MUIButton'
import { MUITooltip } from 'components/common/MUITooltip'
import { SectionedBorderBox } from 'components/common/SectionedBorderBox'
import { SystemMessage } from 'components/common/SystemMessage'
import { isImageUploadProduct } from 'utils/product'
import { useSnackbar } from 'components/contexts/SnackbarService.context'

/** Required visual upload controller */
export const RequiredVisualsUpload: FC = () => {
  const { t } = useTranslation(['ct_upload_page'])
  const {
    fileCtrl,
    assignmentProduct,
    requiredVisualAmount,
    isOverRequiredLimit,
    gridItemSize,
    requiredVisualsForDisplay,
    droppedRequiredVisualsAmount,
    imagesForPopupPreview,
    timerCtrl,
    logTimedUpload,
    setExpandedImageIndex,
  } = useCreativeUploadPage()

  const { spawnWarningToast } = useSnackbar()

  const hasBeenWarned = useRef(false)
  useEffect(() => {
    if (isOverRequiredLimit && !hasBeenWarned.current) {
      spawnWarningToast(
        t('upload_limit_warning_message', { count: requiredVisualAmount }),
        {
          title: t('upload_limit_warning_title'),
          customId: 'CT_UPLOAD_OVER_LIMIT_WARNING',
          timeout: 9000,
        }
      )

      hasBeenWarned.current = true
    }

    if (!isOverRequiredLimit) hasBeenWarned.current = false

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOverRequiredLimit, requiredVisualsForDisplay.length])

  return (
    <Stack gap={3}>

      <Typography
        variant="h2"
        textAlign="left"
        width="100%"
        lineHeight="normal"
        fontWeight={600}
      >
        {t('required_upload.title')}
      </Typography>

      <SystemMessage
        sx={{ width: '100%' }}
        message={(
          <Typography fontSize="1.4rem" component="span">
            <Trans
              t={t}
              i18nKey="required_upload.info_message"
              components={{
                list: <ul style={{ padding: '0 2rem' }} />,
                item: <li />,
              }}
              values={{ count: requiredVisualAmount }}
            >
              &nbsp;
            </Trans>
          </Typography>
        )}
      />

      <SectionedBorderBox
        title={
          <Typography variant="h3" fontWeight={500} lineHeight="1.3">
            <Trans
              t={t}
              i18nKey="required_upload.upload_title"
              components={{
                divider: <Circle fontSize="small" htmlColor={BEIGE_700} />
              }}
              values={{
                uploaded: requiredVisualsForDisplay.length ?? 0,
                limit: requiredVisualAmount,
              }}
            >
              &nbsp;
            </Trans>
          </Typography>
        }
        position="relative"
        p={2}
      >

        <Stack gap={2}>

          <FileAPIUpload
            showFileListing={false}
            dropzoneText={
              <Trans t={t} i18nKey="dropbox_text">
                &nbsp;
              </Trans>
            }
            // The plugin lists only these products (AssignmentSync.isImageUpload)
            dropzoneFooter={!!assignmentProduct && isImageUploadProduct(assignmentProduct) && <LightroomPublishRow />}
            files={requiredVisualsForDisplay}
            acceptedFileTypes="any"
            onCheckFileSize={(size) => size > 0}
            uploadHandler={(acceptedFiles) => {
              const startedDuringOtherUpload = fileCtrl.isAnyUploadRunning
              const timer = timerCtrl.startEventTimer()

              fileCtrl.uploadFiles(acceptedFiles, {
                tag: CreativeUploadFolder.REQUIRED_VISUALS,
                onSettled: () => {
                  timer.stopTimer()
                  logTimedUpload(acceptedFiles, CreativeUploadFolder.REQUIRED_VISUALS, timer.delta!, startedDuringOtherUpload)
                },
              })
            }}
          />

          <Grid container spacing={1}>
            {!!requiredVisualsForDisplay.length && requiredVisualsForDisplay.map((file) => (
              <Grid item key={file.id} xs={gridItemSize}>
                <CreativeUploadImageItem
                  onClick={() => {
                    const index = imagesForPopupPreview.findIndex(entry => entry.id === file.id)

                    if (index >= 0) setExpandedImageIndex(index)
                  }}
                  config={{ allowDelete: true, allowRetry: true, allowReplace: true }}
                  file={file}
                  fileCtrl={fileCtrl}
                  leftActionsSlot={droppedRequiredVisualsAmount > requiredVisualAmount && file.action === FileAction.LOAD && file.state !== FileState.RUNNING &&
                    <MUITooltip content={t('required_upload.move_to_additional')}>
                      <MUIButton
                        size="sm"
                        isIconButton
                        onClick={(e) => {
                          e.stopPropagation()
                          fileCtrl.editFile(file.id, { tag: CreativeUploadFolder.ADDITIONAL_VISUALS })
                        }}
                      >
                        <DriveFileMoveOutlined fontSize="large" />
                      </MUIButton>
                    </MUITooltip>
                  }
                />
              </Grid>
            ))}
          </Grid>

        </Stack>
      </SectionedBorderBox>

    </Stack>
  )
}
